import { useEffect, useMemo, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeft, CheckCircle, Download, Edit, Plus, Star, Trash2, RefreshCw, Camera, FileText, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { api } from '@/lib/api'
import { formatDate, formatFileSize, getStatusColor, getStatusText, buildAuthorizedUrl } from '@/lib/utils'
import type { ApiError, BookAsset, BookDetail } from '@/types'

interface UploadState {
  file: File | null
  error: string
  uploading: boolean
}

type FeedbackType = 'success' | 'error'
interface Feedback { type: FeedbackType; message: string }

const validFormats: Record<'epub' | 'pdf', string> = {
  epub: 'application/epub+zip',
  pdf: 'application/pdf',
}

function resolveFormat(file: File): 'epub' | 'pdf' | null {
  const lowerName = file.name.toLowerCase()
  if (lowerName.endsWith('.epub')) return 'epub'
  if (lowerName.endsWith('.pdf')) return 'pdf'
  return null
}

export function BookDetailPage() {
  const { bookId } = useParams<{ bookId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [uploadState, setUploadState] = useState<UploadState>({ file: null, error: '', uploading: false })
  const [coverUploading, setCoverUploading] = useState(false)
  const coverInputRef = useRef<HTMLInputElement>(null)
  const [feedback, setFeedback] = useState<Feedback | null>(null)
  const [downloadingAssetId, setDownloadingAssetId] = useState<string | null>(null)

  useEffect(() => {
    if (!feedback) return
    const timer = window.setTimeout(() => setFeedback(null), 4000)
    return () => window.clearTimeout(timer)
  }, [feedback])

  const resolveErrorMessage = (err: unknown, fallback: string): string => {
    if (!err) return fallback
    if (typeof err === 'object' && 'message' in err) {
      const msg = (err as { message: unknown }).message
      if (typeof msg === 'string' && msg.length > 0) return msg
    }
    return fallback
  }

  const { data: book, isLoading, error, refetch } = useQuery({
    queryKey: ['book', bookId],
    queryFn: () => {
      if (!bookId) throw new Error('缺少书籍 ID')
      return api.getBook(bookId)
    },
    enabled: !!bookId,
  })

  const invalidateBook = () => {
    if (bookId) {
      queryClient.invalidateQueries({ queryKey: ['book', bookId] })
    }
  }

  const setPrimaryMutation = useMutation({
    mutationFn: ({ assetId }: { assetId: string }) => {
      if (!bookId) throw new Error('缺少书籍 ID')
      return api.updateAsset(bookId, assetId, { isPrimary: true })
    },
    onSuccess: invalidateBook,
  })

  const deleteAssetMutation = useMutation({
    mutationFn: ({ assetId }: { assetId: string }) => {
      if (!bookId) throw new Error('缺少书籍 ID')
      return api.deleteAsset(bookId, assetId)
    },
    onSuccess: invalidateBook,
  })

  const formatAssets = useMemo(() => {
    if (!book?.assets) return []
    return Object.entries(book.assets) as [string, BookAsset[]][]
  }, [book?.assets])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null
    setUploadState({ file, error: '', uploading: false })
  }

  const handleUpload = async () => {
    if (!bookId) return
    const file = uploadState.file
    if (!file) {
      setUploadState((prev) => ({ ...prev, error: '请选择要上传的文件' }))
      return
    }

    const format = resolveFormat(file)
    if (!format) {
      setUploadState((prev) => ({ ...prev, error: '不支持的文件格式' }))
      return
    }

    const expectedType = validFormats[format]
    if (file.type && file.type !== expectedType) {
      setUploadState((prev) => ({ ...prev, error: `文件类型应为 ${expectedType}` }))
      return
    }

    if (file.size > 200 * 1024 * 1024) {
      setUploadState((prev) => ({ ...prev, error: '文件大小不能超过 200MB' }))
      return
    }

    setUploadState((prev) => ({ ...prev, uploading: true, error: '' }))

    try {
      const initResult = await api.initUpload(bookId, {
        format,
        filename: file.name,
        contentType: file.type,
      })

      const uploadResult = await api.uploadFile(bookId, file, format, initResult.r2Key)

      const isPrimaryForFormat = !formatAssets.some(([f]) => f === format)
      await api.registerAsset(bookId, {
        format,
        r2Key: initResult.r2Key,
        filename: file.name,
        sizeBytes: file.size,
        contentType: file.type,
        isPrimary: isPrimaryForFormat,
        contentHash: uploadResult?.contentHash ?? null,
      })

      setUploadState({ file: null, error: '', uploading: false })
      const fileInput = document.getElementById('file-upload') as HTMLInputElement
      if (fileInput) fileInput.value = ''

      invalidateBook()
    } catch (err) {
      const apiError = err as ApiError
      setUploadState((prev) => ({
        ...prev,
        uploading: false,
        error: apiError?.message || '上传失败，请稍后再试',
      }))
    }
  }

  const handleDeleteAsset = async (assetId: string) => {
    if (!confirm('确定要删除该资产吗？此操作无法撤销。')) return
    try {
      await deleteAssetMutation.mutateAsync({ assetId })
      setFeedback({ type: 'success', message: '资产已删除' })
    } catch (err) {
      setFeedback({ type: 'error', message: resolveErrorMessage(err, '删除资产失败，请稍后重试') })
    }
  }

  const handleDownloadAsset = async (assetId: string) => {
    if (!bookId) return
    setDownloadingAssetId(assetId)
    try {
      const { downloadUrl } = await api.getAssetDownloadUrl(bookId, assetId)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (err) {
      setFeedback({ type: 'error', message: resolveErrorMessage(err, '下载文件失败，请稍后重试') })
    } finally {
      setDownloadingAssetId(null)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">加载书籍详情...</h1>
          </div>
        </div>
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            正在加载，请稍候
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error || !book) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            <h1 className="text-2xl font-bold">书籍详情加载失败</h1>
          </div>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            返回
          </Button>
        </div>
        <Card>
          <CardContent className="space-y-4">
            <p className="text-gray-600">无法获取书籍信息，请检查网络或稍后重试。</p>
            <Button onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              重新加载
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const coverImageUrl = buildAuthorizedUrl(book.coverUrl) ?? book.coverUrl ?? undefined

  return (
    <div className="max-w-7xl mx-auto">
      {/* Feedback Banner */}
      {feedback && (
        <div
          className={`mb-4 flex items-center gap-2 rounded-lg border px-4 py-3 text-sm ${
            feedback.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {feedback.type === 'success' ? (
            <CheckCircle className="h-4 w-4 flex-shrink-0" />
          ) : (
            <XCircle className="h-4 w-4 flex-shrink-0" />
          )}
          <span>{feedback.message}</span>
        </div>
      )}
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{book.title}</h1>
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${getStatusColor(book.status)}`}
              >
                {getStatusText(book.status)}
              </span>
              <span
                className="inline-flex items-center rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-xs font-medium text-purple-700"
                title="数字越小权限越高，默认 100 表示普通用户可访问"
              >
                🔐 等级 L{book.requiredLevel ?? 0}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {book.author ? `作者：${book.author} · ` : ''}
              更新于 {formatDate(book.updatedAt)}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              返回列表
            </Button>
            <Button onClick={() => navigate(`/books/${book.id}/edit`)}>
              <Edit className="mr-2 h-4 w-4" />
              编辑书籍
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Cover and Quick Info */}
        <div className="lg:col-span-1 space-y-6">
          {/* Cover Card */}
          <Card>
            <CardContent className="p-6">
              <div className="relative group mx-auto w-full max-w-[240px]">
                {coverImageUrl ? (
                  <div className="w-full aspect-[2/3] overflow-hidden rounded-lg border shadow-lg">
                    <img src={coverImageUrl} alt="书籍封面" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => coverInputRef.current?.click()}
                        disabled={coverUploading}
                      >
                        <Camera className="h-4 w-4" />
                        更换
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={async () => {
                          if (confirm('确定要删除封面吗？')) {
                            try {
                              await api.deleteCover(bookId!)
                              invalidateBook()
                              setFeedback({ type: 'success', message: '封面已删除' })
                            } catch (err) {
                              setFeedback({ type: 'error', message: resolveErrorMessage(err, '删除封面失败') })
                            }
                          }
                        }}
                        disabled={coverUploading}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="w-full aspect-[2/3] flex items-center justify-center rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-500 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => coverInputRef.current?.click()}
                  >
                    <div className="text-center p-4">
                      <Camera className="h-10 w-10 mx-auto mb-2 text-gray-400" />
                      <p className="text-gray-600">上传封面</p>
                      <p className="text-xs text-gray-400 mt-1">点击选择图片</p>
                    </div>
                  </div>
                )}
                <input
                  ref={coverInputRef}
                  type="file"
                  className="hidden"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={async (e) => {
                    const file = e.target.files?.[0]
                    if (!file) return

                    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
                      setFeedback({ type: 'error', message: '只支持 JPG、PNG、WebP 格式的图片' })
                      return
                    }

                    if (file.size > 5 * 1024 * 1024) {
                      setFeedback({ type: 'error', message: '图片文件不能超过 5MB' })
                      return
                    }

                    setCoverUploading(true)
                    try {
                      const response = await api.uploadCover(bookId!, file)
                      queryClient.setQueryData(['book', bookId], (prev: BookDetail | undefined) =>
                        prev ? { ...prev, coverUrl: response.coverUrl } : prev,
                      )
                      invalidateBook()
                      setFeedback({ type: 'success', message: '封面上传成功' })
                    } catch (err) {
                      setFeedback({ type: 'error', message: resolveErrorMessage(err, '封面上传失败') })
                    } finally {
                      setCoverUploading(false)
                      e.target.value = ''
                    }
                  }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Quick Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">快速信息</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-gray-500">作者</p>
                <p className="text-sm font-medium">{book.author || '未知'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">ISBN</p>
                <p className="text-sm font-medium font-mono">{book.isbn || '未填写'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">可用格式</p>
                {book.availableFormats.length > 0 ? (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {book.availableFormats.map(format => (
                      <span key={format} className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                        {format.toUpperCase()}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">暂无文件</p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500">创建时间</p>
                <p className="text-sm">{formatDate(book.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">更新时间</p>
                <p className="text-sm">{formatDate(book.updatedAt)}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">书籍简介</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap text-gray-700">
                {book.summary?.trim() || '暂无简介'}
              </p>
            </CardContent>
          </Card>

          {/* Tags and Themes Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">分类与标签</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-gray-500 mb-2">主题分类</p>
                {book.themes && book.themes.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {book.themes.map((theme) => (
                      <span
                        key={theme.id}
                        className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium group relative"
                        style={{
                          backgroundColor: theme.color ? `${theme.color}15` : '#f3f4f6',
                          color: theme.color || '#374151',
                          border: `1px solid ${theme.color || '#d1d5db'}`,
                        }}
                        title={theme.path || theme.name}
                      >
                        {theme.displayName || theme.name}
                        {(theme.level || 0) > 0 && (
                          <span className="ml-1 opacity-60 text-xs">
                            L{theme.level}
                          </span>
                        )}
                        {/* Tooltip 显示完整路径 */}
                        {theme.path && theme.path !== `/${theme.id}` && (
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                            {theme.path.split('/').filter(p => p).join(' / ')}
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-2 h-2 bg-gray-800 rotate-45"></div>
                          </div>
                        )}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">暂无主题</p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-2">标签</p>
                {book.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {book.tags.map((tag) => (
                      <span key={tag} className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">暂无标签</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Assets Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">资产管理</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => invalidateBook()} disabled={isLoading}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {formatAssets.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-500 mb-4">暂无资产文件</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {formatAssets.map(([format, assets]) => (
                    <div key={format}>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">
                        {format.toUpperCase()} 文件 ({assets.length})
                      </h3>
                      <div className="space-y-2">
                        {assets.map((asset) => (
                          <div
                            key={asset.id}
                            className="flex items-center justify-between p-3 rounded-lg border bg-gray-50 hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-gray-400" />
                                <span className="text-sm font-medium">
                                  {asset.filename || '未命名文件'}
                                </span>
                                {asset.isPrimary && (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                    <Star className="h-3 w-3" />主文件
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-1 ml-6">
                                {formatFileSize(asset.sizeBytes || 0)} · 上传于 {formatDate(asset.createdAt)}
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadAsset(asset.id)}
                                disabled={downloadingAssetId === asset.id}
                                title="下载文件"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              {!asset.isPrimary && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setPrimaryMutation.mutate({ assetId: asset.id })}
                                  disabled={setPrimaryMutation.isPending}
                                  title="设为主文件"
                                >
                                  <Star className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteAsset(asset.id)}
                                disabled={deleteAssetMutation.isPending}
                                title="删除文件"
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload New Asset */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="text-sm font-medium text-gray-700 mb-3">添加新文件</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <input
                      id="file-upload"
                      type="file"
                      accept=".pdf,.epub"
                      onChange={handleFileChange}
                      className="flex-1 text-sm text-gray-600 file:mr-3 file:rounded-md file:border file:border-gray-300 file:bg-white file:px-3 file:py-2 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-50"
                    />
                    <Button
                      onClick={handleUpload}
                      disabled={!uploadState.file || uploadState.uploading}
                      loading={uploadState.uploading}
                      size="sm"
                    >
                      <Plus className="mr-1 h-3 w-3" />上传
                    </Button>
                  </div>
                  {uploadState.error && (
                    <p className="text-xs text-red-600">{uploadState.error}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    支持 PDF 和 EPUB 格式，最大 200MB
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default BookDetailPage
